import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useApp } from '../contexts/App';
import { Layout } from '../components/Layout';
import Loading from '../components/Loading';
import NotificationConfig from '../pages/setting/notification/edit';
import { usePermission } from '../hooks/usePermission';

const Auth = React.lazy(() => import('../pages/auth'));
const Home = React.lazy(() => import('../pages/home'));

const AdminList = React.lazy(() => import('../pages/admin/list'));
const AdminEdit = React.lazy(() => import('../pages/admin/edit'));

const RoleList = React.lazy(() => import('../pages/role/list'));
const RoleEdit = React.lazy(() => import('../pages/role/edit'));

const UserList = React.lazy(() => import('../pages/user/list'));
const UserEdit = React.lazy(() => import('../pages/user/edit'));

const AttributeEdit = React.lazy(() => import('../pages/attribute/edit'));
const AttributeList = React.lazy(() => import('../pages/attribute/list'));

const TrailEdit = React.lazy(() => import('../pages/trail/edit'));
const TrailList = React.lazy(() => import('../pages/trail/list'));

const MarathonTrailList = React.lazy(() => import('../pages/marathon-trail/list'));
const MarathonTrailEdit = React.lazy(() => import('../pages/marathon-trail/edit'));
const MarathonTrailCreate = React.lazy(() => import('../pages/marathon-trail/edit'));

const StayList = React.lazy(() => import('../pages/stay/list'));
const StayEdit = React.lazy(() => import('../pages/stay/edit'));
const StayCreate = React.lazy(() => import('../pages/stay/edit'));

const EatLocalList = React.lazy(() => import('../pages/eat-local/list'));
const EatLocalEdit = React.lazy(() => import('../pages/eat-local/edit'));
const EatLocalCreate = React.lazy(() => import('../pages/eat-local/edit'));

const TrailUpdateHistory = React.lazy(() => import('../pages/trail/history'));
const ReviewEdit = React.lazy(() => import('../pages/review/edit'));
const ReviewList = React.lazy(() => import('../pages/review/list'));

const PhotoEdit = React.lazy(() => import('../pages/photo/edit'));
const PhotoList = React.lazy(() => import('../pages/photo/list'));

const ActivityEdit = React.lazy(() => import('../pages/activity/edit'));
const ActivityList = React.lazy(() => import('../pages/activity/list'));

const SettingAdsEdit = React.lazy(() => import('../pages/setting/ads/edit'));
const AppVersionConfig = React.lazy(() => import('../pages/setting/version/edit'));
const AppSettingConfig = React.lazy(() => import('../pages/setting/setting/edit'));
const GpsSettingConfig = React.lazy(() => import('../pages/setting/gps/edit'));
const CrawlEdit = React.lazy(() => import('../pages/crawl/edit'));
const CrawlList = React.lazy(() => import('../pages/crawl/list'));

const ChallengeEdit = React.lazy(() => import('../pages/challenge/edit'));
const ChallengeList = React.lazy(() => import('../pages/challenge/list'));

const RankingList = React.lazy(() => import('../pages/ranking/list'));

const MembershipList = React.lazy(() => import('../pages/membership/list'));
const MembershipEdit = React.lazy(() => import('../pages/membership/edit'));

const TicketList = React.lazy(() => import('../pages/ticket/list'));
const MyTicketList = React.lazy(() => import('../pages/ticket/my-list'));
const TicketEdit = React.lazy(() => import('../pages/ticket/edit'));

const ExoseedList = React.lazy(() => import('../pages/exoseed/list'));
const ExoseedEdit = React.lazy(() => import('../pages/exoseed/edit'));

const EventList = React.lazy(() => import('../pages/event/list'));
const EventEdit = React.lazy(() => import('../pages/event/edit'));

const BadgeList = React.lazy(() => import('../pages/exoseed/badges/list'));
const BadgeEdit = React.lazy(() => import('../pages/exoseed/badges/edit'));
const RewardList = React.lazy(() => import('../pages/exoseed/rewards/list'));
const RewardEdit = React.lazy(() => import('../pages/exoseed/rewards/edit'));
const StampList = React.lazy(() => import('../pages/exoseed/stamps/list'));
const StampEdit = React.lazy(() => import('../pages/exoseed/stamps/edit'));
const OfferList = React.lazy(() => import('../pages/exoseed/offers/list'));
const OfferEdit = React.lazy(() => import('../pages/exoseed/offers/edit'));

let PREV_URL = window.location.href.replace(origin, '');

// Component for unauthorized access page
const UnauthorizedPage = () => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
            textAlign: 'center',
        }}
    >
        <h1 style={{ color: '#ff4d4f', marginBottom: '16px' }}>Access Denied</h1>
        <p style={{ color: '#666', marginBottom: '24px' }}>You do not have permission to access this page.</p>
        <a href="/trail/trails" style={{ color: '#016562', textDecoration: 'underline' }}>
            Go to Home
        </a>
    </div>
);

// Protected Route Component
const ProtectedRoute = ({ element: Element, permissions = [] }: { element: React.ComponentType; permissions?: string[] }) => {
    const { isSuperAdmin, hasAnyPermission } = usePermission();

    // Super admins or users with no permission requirements can access
    if (isSuperAdmin() || permissions.length === 0) {
        return <Element />;
    }

    // Check if user has any of the required permissions
    if (hasAnyPermission(permissions)) {
        return <Element />;
    }

    // User doesn't have permission
    return <UnauthorizedPage />;
};

export const routers = [
    {
        path: '/',
        element: Home,
        meta: {
            title: 'Home',
            breadcrumb: [{ path: null, breadcrumbName: 'Home' }],
        },
        permissions: [],
    },
    {
        path: '/user/admins',
        element: AdminList,
        meta: {
            title: 'Admins',
            breadcrumb: [{ path: null, breadcrumbName: 'Admins' }],
        },
        permissions: ['admins.view'],
    },
    {
        path: '/user/admins/update/:id',
        element: AdminEdit,
        meta: {
            title: 'Update Admin',
            breadcrumb: [
                { path: '/user/admins', breadcrumbName: 'Admins' },
                { path: null, breadcrumbName: 'Update admin' },
            ],
        },
        permissions: ['admins.edit'],
    },
    {
        path: '/user/admins/create',
        element: AdminEdit,
        meta: {
            title: 'Create Admin',
            breadcrumb: [
                { path: '/user/admins', breadcrumbName: 'Admins' },
                { path: null, breadcrumbName: 'Create Admin' },
            ],
        },
        permissions: ['admins.create'],
    },
    {
        path: '/user/roles',
        element: RoleList,
        meta: {
            title: 'Roles',
            breadcrumb: [{ path: null, breadcrumbName: 'Roles' }],
        },
        permissions: ['roles.view'],
    },
    {
        path: '/user/roles/update/:id',
        element: RoleEdit,
        meta: {
            title: 'Update Role',
            breadcrumb: [
                { path: '/user/roles', breadcrumbName: 'Roles' },
                { path: null, breadcrumbName: 'Update Role' },
            ],
        },
        permissions: ['roles.edit'],
    },
    {
        path: '/user/roles/create',
        element: RoleEdit,
        meta: {
            title: 'Create Role',
            breadcrumb: [
                { path: '/user/roles', breadcrumbName: 'Roles' },
                { path: null, breadcrumbName: 'Create Role' },
            ],
        },
        permissions: ['roles.create'],
    },
    {
        path: '/user/customers',
        element: UserList,
        meta: {
            title: 'Customers',
            breadcrumb: [{ path: null, breadcrumbName: 'Customers' }],
        },
        permissions: ['users.view'],
    },
    {
        path: '/user',
        element: () => <Navigate replace to="/user/customers" />,
        meta: {
            title: 'Customers',
            breadcrumb: [{ path: null, breadcrumbName: 'Customers' }],
        },
        permissions: ['users.view'],
    },
    {
        path: '/user/customers/update/:id',
        element: UserEdit,
        meta: {
            title: 'Update Customer',
            breadcrumb: [
                { path: '/user/customers', breadcrumbName: 'Customers' },
                { path: null, breadcrumbName: 'Update Customer' },
            ],
        },
        permissions: ['users.edit'],
    },
    {
        path: '/user/customers/create',
        element: UserEdit,
        meta: {
            title: 'Add Customer',
            breadcrumb: [
                { path: '/user/customers', breadcrumbName: 'Customers' },
                { path: null, breadcrumbName: 'Add Customer' },
            ],
        },
        permissions: ['users.create'],
    },
    {
        path: '/trail/attributes',
        element: AttributeList,
        meta: {
            title: 'Attributes',
            breadcrumb: [{ path: null, breadcrumbName: 'Attributes' }],
        },
        permissions: ['settings.attributes'],
    },
    {
        path: '/trail/attributes/update/:id',
        element: AttributeEdit,
        meta: {
            title: 'Update Attribute',
            breadcrumb: [
                { path: '/trail/attributes', breadcrumbName: 'Attributes' },
                { path: null, breadcrumbName: 'Update Attribute' },
            ],
        },
        permissions: ['settings.attributes'],
    },
    {
        path: '/trail/attributes/create',
        element: AttributeEdit,
        meta: {
            title: 'Add Attribute',
            breadcrumb: [
                { path: '/trail/attributes', breadcrumbName: 'Attributes' },
                { path: null, breadcrumbName: 'Add Attribute' },
            ],
        },
        permissions: ['settings.attributes'],
    },
    {
        path: '/trail/trails',
        element: TrailList,
        meta: {
            title: 'Trails',
            breadcrumb: [{ path: null, breadcrumbName: 'Trails' }],
        },
        permissions: ['trails.view'],
    },
    {
        path: '/trail',
        element: () => <Navigate replace to="/trail/trails" />,
        meta: {
            title: 'Trails',
            breadcrumb: [{ path: null, breadcrumbName: 'Trails' }],
        },
        permissions: ['trails.view'],
    },
    {
        path: '/trail/trails/update/:id',
        element: TrailEdit,
        meta: {
            title: 'Update Trail',
            breadcrumb: [
                { path: '/trail/trails', breadcrumbName: 'Trails' },
                { path: null, breadcrumbName: 'Update Trail' },
            ],
        },
        permissions: ['trails.edit'],
    },
    {
        path: '/trail/trails/history/:trailId/:historyId',
        element: TrailUpdateHistory,
        meta: {
            title: 'Route old version',
            breadcrumb: [
                { path: '/trail/trails', breadcrumbName: 'Trails' },
                { path: null, breadcrumbName: 'Route old version' },
            ],
        },
        permissions: ['trails.view'],
    },
    {
        path: '/trail/trails/create',
        element: TrailEdit,
        meta: {
            title: 'Add Trail',
            breadcrumb: [
                { path: '/trail/trails', breadcrumbName: 'Trails' },
                { path: null, breadcrumbName: 'Add Trail' },
            ],
        },
        permissions: ['trails.create'],
    },

    {
        path: '/trail/marathon-trail',
        element: MarathonTrailList,
        meta: {
            title: 'Marathon Trails',
            breadcrumb: [{ path: null, breadcrumbName: 'Marathon Trails' }],
        },
        permissions: ['marathon_trails.view'],
    },
    {
        path: '/trail/marathon-trail/create',
        element: MarathonTrailCreate,
        meta: {
            title: 'Create Marathon Trail',
            breadcrumb: [
                {
                    path: '/trail/marathon-trail',
                    breadcrumbName: 'Marathon Trails',
                },
                { path: null, breadcrumbName: 'Create Marathon Trail' },
            ],
        },
        permissions: ['marathon_trails.create'],
    },
    {
        path: '/trail/marathon-trail/update/:id',
        element: MarathonTrailEdit,
        meta: {
            title: 'Update Marathon Trail',
            breadcrumb: [
                {
                    path: '/trail/marathon-trail',
                    breadcrumbName: 'Marathon Trail',
                },
                { path: null, breadcrumbName: 'Update Marathon Trail' },
            ],
        },
        permissions: ['marathon_trails.edit'],
    },

    {
        path: '/trail/stay',
        element: StayList,
        meta: {
            title: 'Stay',
            breadcrumb: [{ path: null, breadcrumbName: 'Stay' }],
        },
        permissions: ['stay.view'],
    },
    {
        path: '/trail/stay/create',
        element: StayCreate,
        meta: {
            title: 'Create Stay Location',
            breadcrumb: [
                { path: '/trail/stay', breadcrumbName: 'Stay' },
                { path: null, breadcrumbName: 'Create Stay' },
            ],
        },
        permissions: ['stay.create'],
    },
    {
        path: '/trail/stay/update/:id',
        element: StayEdit,
        meta: {
            title: 'Update Stay',
            breadcrumb: [
                { path: '/trail/stay', breadcrumbName: 'Stay' },
                { path: null, breadcrumbName: 'Update Stay' },
            ],
        },
        permissions: ['stay.edit'],
    },

    {
        path: '/trail/eat-local',
        element: EatLocalList,
        meta: {
            title: 'Eat local',
            breadcrumb: [{ path: null, breadcrumbName: 'Eat local' }],
        },
        permissions: ['eat_local.view'],
    },
    {
        path: '/trail/eat-local/create',
        element: EatLocalCreate,
        meta: {
            title: 'CREATE EAT LOCAL LOCATION',
            breadcrumb: [
                { path: '/trail/eat-local', breadcrumbName: 'Eat Local' },
                { path: null, breadcrumbName: 'Create Eat Local' },
            ],
        },
        permissions: ['eat_local.create'],
    },
    {
        path: '/trail/eat-local/update/:id',
        element: EatLocalEdit,
        meta: {
            title: 'Update EAT LOCAL LOCATION',
            breadcrumb: [
                { path: '/trail/eat-local', breadcrumbName: 'Eat Local' },
                { path: null, breadcrumbName: 'Update Eat Local' },
            ],
        },
        permissions: ['eat_local.edit'],
    },

    {
        path: '/trail/reviews',
        element: ReviewList,
        meta: {
            title: 'Reviews',
            breadcrumb: [{ path: null, breadcrumbName: 'Reviews' }],
        },
        permissions: ['reviews.view'],
    },
    {
        path: '/trail/reviews/update/:id',
        element: ReviewEdit,
        meta: {
            title: 'Update Review',
            breadcrumb: [
                { path: '/trail/reviews', breadcrumbName: 'Reviews' },
                { path: null, breadcrumbName: 'Update Review' },
            ],
        },
        permissions: ['reviews.edit'],
    },
    {
        path: '/trail/reviews/create',
        element: ReviewEdit,
        meta: {
            title: 'Add Review',
            breadcrumb: [
                { path: '/trail/reviews', breadcrumbName: 'Reviews' },
                { path: null, breadcrumbName: 'Add Review' },
            ],
        },
        permissions: ['reviews.create'],
    },

    {
        path: '/trail/photos',
        element: PhotoList,
        meta: {
            title: 'Photos',
            breadcrumb: [{ path: null, breadcrumbName: 'Photos' }],
        },
        permissions: ['photos.view'],
    },
    {
        path: '/trail/photos/update/:id',
        element: PhotoEdit,
        meta: {
            title: 'Update Photo',
            breadcrumb: [
                { path: '/trail/photos', breadcrumbName: 'Photos' },
                { path: null, breadcrumbName: 'Update Photo' },
            ],
        },
        permissions: ['photos.edit'],
    },
    {
        path: '/trail/photos/create',
        element: PhotoEdit,
        meta: {
            title: 'Add Photos',
            breadcrumb: [
                { path: '/trail/photos', breadcrumbName: 'Photos' },
                { path: null, breadcrumbName: 'Add Photo' },
            ],
        },
        permissions: ['photos.create'],
    },

    {
        path: '/activities',
        element: ActivityList,
        meta: {
            title: 'Activities',
            breadcrumb: [{ path: null, breadcrumbName: 'Activities' }],
        },
        permissions: ['activities.view'],
    },
    {
        path: '/activities/update/:id',
        element: ActivityEdit,
        meta: {
            title: 'Update Activity',
            breadcrumb: [
                { path: '/activities', breadcrumbName: 'Activities' },
                { path: null, breadcrumbName: 'Update Activity' },
            ],
        },
        permissions: ['activities.edit'],
    },
    {
        path: '/activities/create',
        element: ActivityEdit,
        meta: {
            title: 'Add Activity',
            breadcrumb: [
                { path: '/activities', breadcrumbName: 'Activities' },
                { path: null, breadcrumbName: 'Add Activity' },
            ],
        },
        permissions: ['activities.create'],
    },

    {
        path: '/ranking',
        element: RankingList,
        meta: {
            title: 'User Activity Ranking',
            breadcrumb: [{ path: null, breadcrumbName: 'Ranking' }],
        },
        permissions: ['ranking.view'],
    },

    {
        path: '/trail/challenges',
        element: ChallengeList,
        meta: {
            title: 'Trackmates',
            breadcrumb: [{ path: null, breadcrumbName: 'Trackmates' }],
        },
        permissions: ['challenges.view'],
    },
    {
        path: '/trail/challenges/details/:id',
        element: ChallengeEdit,
        meta: {
            title: 'Trackmate Details',
            breadcrumb: [
                { path: '/trail/challenges', breadcrumbName: 'Trackmates' },
                { path: null, breadcrumbName: 'Trackmate Details' },
            ],
        },
        permissions: ['challenges.view'],
    },
    {
        path: '/setting',
        element: () => <Navigate replace to="/setting/ads" />,
        meta: {
            title: 'Setting',
            breadcrumb: [{ path: null, breadcrumbName: 'Setting' }],
        },
        permissions: ['settings.ads', 'settings.app_version', 'settings.app_setting', 'settings.notification', 'settings.gps'],
    },
    {
        path: '/setting/ads',
        element: SettingAdsEdit,
        meta: {
            title: 'Ad config',
            breadcrumb: [
                { path: '/setting', breadcrumbName: 'Setting' },
                { path: null, breadcrumbName: 'AD' },
            ],
        },
        permissions: ['settings.ads'],
    },
    {
        path: '/setting/app-version',
        element: AppVersionConfig,
        meta: {
            title: 'App version config',
            breadcrumb: [
                { path: '/setting', breadcrumbName: 'Setting' },
                { path: null, breadcrumbName: 'App version' },
            ],
        },
        permissions: ['settings.app_version'],
    },
    {
        path: '/setting/app-setting',
        element: AppSettingConfig,
        meta: {
            title: 'App setting config',
            breadcrumb: [
                { path: '/setting', breadcrumbName: 'Setting' },
                { path: null, breadcrumbName: 'App setting' },
            ],
        },
        permissions: ['settings.app_setting'],
    },
    {
        path: '/setting/app-notification',
        element: NotificationConfig,
        meta: {
            title: 'notification',
            breadcrumb: [
                { path: '/setting', breadcrumbName: 'Setting' },
                { path: null, breadcrumbName: 'Notification' },
            ],
        },
        permissions: ['settings.notification'],
    },
    {
        path: '/setting/gps',
        element: GpsSettingConfig,
        meta: {
            title: 'GPS Settings',
            breadcrumb: [{ path: null, breadcrumbName: 'GPS Settings' }],
        },
        permissions: ['settings.gps'],
    },
    {
        path: '/crawl',
        element: CrawlList,
        meta: {
            title: 'Crawl management',
            breadcrumb: [{ path: null, breadcrumbName: 'Crawl management' }],
        },
        permissions: ['crawl.view'],
    },
    {
        path: '/crawl/update/:id',
        element: CrawlEdit,
        meta: {
            title: 'Update Destination',
            breadcrumb: [
                { path: '/crawl', breadcrumbName: 'Crawl' },
                { path: null, breadcrumbName: 'Update' },
            ],
        },
        permissions: ['crawl.edit'],
    },
    {
        path: '/membership',
        element: MembershipList,
        meta: {
            title: 'Memberships',
            breadcrumb: [{ path: null, breadcrumbName: 'Memberships' }],
        },
        permissions: ['membership.view'],
    },
    {
        path: '/membership/create',
        element: MembershipEdit,
        meta: {
            title: 'Create Membership',
            breadcrumb: [
                { path: '/membership', breadcrumbName: 'Memberships' },
                { path: null, breadcrumbName: 'Create Membership' },
            ],
        },
        permissions: ['membership.create'],
    },
    {
        path: '/membership/update/:id',
        element: MembershipEdit,
        meta: {
            title: 'Update Membership',
            breadcrumb: [
                { path: '/membership', breadcrumbName: 'Memberships' },
                { path: null, breadcrumbName: 'Update Membership' },
            ],
        },
        permissions: ['membership.edit'],
    },
    {
        path: '/tickets',
        element: TicketList,
        meta: {
            title: 'Support Tickets',
            breadcrumb: [{ path: null, breadcrumbName: 'Support Tickets' }],
        },
        permissions: ['tickets.view'],
    },
    {
        path: '/my-tickets',
        element: MyTicketList,
        meta: {
            title: 'My Tickets',
            breadcrumb: [{ path: null, breadcrumbName: 'My Tickets' }],
        },
        permissions: ['tickets.my_tickets'],
    },
    {
        path: '/tickets/update/:id',
        element: TicketEdit,
        meta: {
            title: 'Ticket Details',
            breadcrumb: [
                { path: '/tickets', breadcrumbName: 'Support Tickets' },
                { path: null, breadcrumbName: 'Ticket Details' },
            ],
        },
        permissions: ['tickets.view', 'tickets.my_tickets'],
    },
    {
        path: '/exoseed',
        element: () => <Navigate replace to="/exoseed/campaigns" />,
        meta: {
            title: 'ExoSeed Campaigns',
            breadcrumb: [
                { path: '/exoseed/campaigns', breadcrumbName: 'ExoSeed' },
                { path: null, breadcrumbName: 'Campaigns' },
            ],
        },
        permissions: ['exoseed.view'],
    },
    {
        path: '/exoseed/campaigns',
        element: ExoseedList,
        meta: {
            title: 'ExoSeed Campaigns',
            breadcrumb: [
                { path: '/exoseed/campaigns', breadcrumbName: 'ExoSeed' },
                { path: null, breadcrumbName: 'Campaigns' },
            ],
        },
        permissions: ['exoseed.view'],
    },
    {
        path: '/exoseed/campaigns/create',
        element: ExoseedEdit,
        meta: {
            title: 'Create Campaign',
            breadcrumb: [
                { path: '/exoseed/campaigns', breadcrumbName: 'ExoSeed' },
                { path: null, breadcrumbName: 'Create Campaign' },
            ],
        },
        permissions: ['exoseed.create'],
    },
    {
        path: '/exoseed/campaigns/update/:id',
        element: ExoseedEdit,
        meta: {
            title: 'Edit Campaign',
            breadcrumb: [
                { path: '/exoseed/campaigns', breadcrumbName: 'ExoSeed' },
                { path: null, breadcrumbName: 'Edit Campaign' },
            ],
        },
        permissions: ['exoseed.edit'],
    },
    {
        path: '/events',
        element: EventList,
        meta: {
            title: 'Events',
            breadcrumb: [{ path: null, breadcrumbName: 'Events' }],
        },
        permissions: ['events.view'],
    },
    {
        path: '/events/create',
        element: EventEdit,
        meta: {
            title: 'Create Event',
            breadcrumb: [
                { path: '/events', breadcrumbName: 'Events' },
                { path: null, breadcrumbName: 'Create Event' },
            ],
        },
        permissions: ['events.create'],
    },
    {
        path: '/events/update/:id',
        element: EventEdit,
        meta: {
            title: 'Edit Event',
            breadcrumb: [
                { path: '/events', breadcrumbName: 'Events' },
                { path: null, breadcrumbName: 'Edit Event' },
            ],
        },
        permissions: ['events.edit'],
    },
    // ExoSeed Badges
    {
        path: '/exoseed/badges',
        element: BadgeList,
        meta: {
            title: 'Badges',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: null, breadcrumbName: 'Badges' },
            ],
        },
    },
    {
        path: '/exoseed/badges/create',
        element: BadgeEdit,
        meta: {
            title: 'Create Badge',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: '/exoseed/badges', breadcrumbName: 'Badges' },
                { path: null, breadcrumbName: 'Create Badge' },
            ],
        },
    },
    {
        path: '/exoseed/badges/update/:id',
        element: BadgeEdit,
        meta: {
            title: 'Edit Badge',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: '/exoseed/badges', breadcrumbName: 'Badges' },
                { path: null, breadcrumbName: 'Edit Badge' },
            ],
        },
    },
    // ExoSeed Rewards
    {
        path: '/exoseed/rewards',
        element: RewardList,
        meta: {
            title: 'Rewards',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: null, breadcrumbName: 'Rewards' },
            ],
        },
    },
    {
        path: '/exoseed/rewards/create',
        element: RewardEdit,
        meta: {
            title: 'Create Reward',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: '/exoseed/rewards', breadcrumbName: 'Rewards' },
                { path: null, breadcrumbName: 'Create Reward' },
            ],
        },
    },
    {
        path: '/exoseed/rewards/update/:id',
        element: RewardEdit,
        meta: {
            title: 'Edit Reward',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: '/exoseed/rewards', breadcrumbName: 'Rewards' },
                { path: null, breadcrumbName: 'Edit Reward' },
            ],
        },
    },
    // ExoSeed Stamps
    {
        path: '/exoseed/stamps',
        element: StampList,
        meta: {
            title: 'Stamps',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: null, breadcrumbName: 'Stamps' },
            ],
        },
    },
    {
        path: '/exoseed/stamps/create',
        element: StampEdit,
        meta: {
            title: 'Create Stamp',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: '/exoseed/stamps', breadcrumbName: 'Stamps' },
                { path: null, breadcrumbName: 'Create Stamp' },
            ],
        },
    },
    {
        path: '/exoseed/stamps/update/:id',
        element: StampEdit,
        meta: {
            title: 'Edit Stamp',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: '/exoseed/stamps', breadcrumbName: 'Stamps' },
                { path: null, breadcrumbName: 'Edit Stamp' },
            ],
        },
    },
    // ExoSeed Offers
    {
        path: '/exoseed/offers',
        element: OfferList,
        meta: {
            title: 'Offers',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: null, breadcrumbName: 'Offers' },
            ],
        },
    },
    {
        path: '/exoseed/offers/create',
        element: OfferEdit,
        meta: {
            title: 'Create Offer',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: '/exoseed/offers', breadcrumbName: 'Offers' },
                { path: null, breadcrumbName: 'Create Offer' },
            ],
        },
    },
    {
        path: '/exoseed/offers/update/:id',
        element: OfferEdit,
        meta: {
            title: 'Edit Offer',
            breadcrumb: [
                { path: '/exoseed', breadcrumbName: 'ExoSeed' },
                { path: '/exoseed/offers', breadcrumbName: 'Offers' },
                { path: null, breadcrumbName: 'Edit Offer' },
            ],
        },
    },
];

function AppRouter() {
    const { user } = useApp();
    const { hasAnyPermission, isSuperAdmin } = usePermission();

    // Determine the first accessible route for the logged-in user
    const firstAccessiblePath = React.useMemo(() => {
        // Super admins can use the first defined non-param route
        if (isSuperAdmin()) {
            const firstRoute = routers.find((r) => r.path && !r.path.includes(':') && r.path !== '/' && !r.path.startsWith('/auth'));
            return firstRoute?.path || '/trail/trails';
        }

        // Check routes in order and return the first that the user can access
        for (const route of routers) {
            // Skip parameterized and auth routes for default landing
            if (!route.path || route.path.includes(':') || route.path.startsWith('/auth') || route.path === '/') continue;

            const permissions = route.permissions || [];
            if (permissions.length === 0 || hasAnyPermission(permissions)) {
                return route.path;
            }
        }

        // Fallback
        return '/auth/login';
    }, [hasAnyPermission, isSuperAdmin]);

    console.log('AppRouter: user state:', user);
    console.log('AppRouter: !!user:', !!user);

    return (
        <Suspense fallback={null}>
            {!!user ? (
                <Router>
                    <Layout>
                        <Routes>
                            <Route path="/auth/*" element={<Navigate replace to={firstAccessiblePath} />} />
                            <Route path="/" element={<Navigate replace to={firstAccessiblePath} />} />
                            {routers.map((value, index) => {
                                const Element = value.element;
                                const permissions = value.permissions || [];
                                return (
                                    <Route
                                        key={index.toString()}
                                        path={value.path}
                                        element={
                                            <React.Suspense fallback={<Loading />}>
                                                <ProtectedRoute element={Element} permissions={permissions} />
                                            </React.Suspense>
                                        }
                                    />
                                );
                            })}
                        </Routes>
                    </Layout>
                </Router>
            ) : (
                <Router>
                    <Routes>
                        <Route path="/*" element={<Navigate replace to="/auth/login" />} />
                        <Route path="/auth" element={<Navigate replace to="/auth/login" />} />
                        <Route
                            path="/auth/login"
                            element={
                                <React.Suspense fallback={<Loading />}>
                                    <Auth redirectUrl={PREV_URL.includes('') ? PREV_URL : null} />
                                </React.Suspense>
                            }
                        />
                    </Routes>
                </Router>
            )}
        </Suspense>
    );
}

export default AppRouter;
